import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  History, Tag, Trash2, Edit2, Loader2, Search, Check,
  Building2, Users, Activity, TrendingUp, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════
// AUDIT TRAIL VIEWER
// ══════════════════════════════════════════════
export function AuditTrailViewer({ token }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ entity: '', action: '' });
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.entity) params.entity = filter.entity;
      if (filter.action) params.action = filter.action;
      const res = await axios.get(`${BACKEND_URL}/api/admin/audit-log`, { headers, params });
      setEntries(res.data.entries || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [headers, filter]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const actionColors = { bulk_update: 'bg-blue-100 text-blue-700', bulk_tag: 'bg-purple-100 text-purple-700', bulk_delete: 'bg-red-100 text-red-700', create: 'bg-emerald-100 text-emerald-700', update: 'bg-amber-100 text-amber-700', delete: 'bg-red-100 text-red-700' };

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-5xl" data-testid="audit-trail">
        <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Audit Trail</h2>
        <p className="text-xs text-muted-foreground mb-4">Complete history of all changes across your organization.</p>
        <div className="flex gap-2 mb-4 flex-wrap">
          <Select value={filter.entity || 'all'} onValueChange={v => setFilter(f => ({ ...f, entity: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="All entities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              {['assignments', 'properties', 'products', 'vendors', 'integrations'].map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.action || 'all'} onValueChange={v => setFilter(f => ({ ...f, action: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {['create', 'update', 'delete', 'bulk_update', 'bulk_tag', 'bulk_delete'].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {loading ? <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> : (
          <div className="border rounded-xl overflow-hidden">
            {entries.length === 0 ? (
              <p className="p-8 text-center text-sm text-muted-foreground">No audit entries found.</p>
            ) : (
              <div className="divide-y">
                {entries.map(e => (
                  <div key={e.id} className="flex items-start gap-3 p-3 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{e.user_email || 'System'}</span>
                        <Badge className={`text-[8px] ${actionColors[e.action] || 'bg-muted'}`}>{e.action}</Badge>
                        <Badge variant="outline" className="text-[8px]">{e.entity}</Badge>
                      </div>
                      <p className="text-muted-foreground mt-0.5">{e.details}</p>
                    </div>
                    <span className="text-[9px] text-muted-foreground shrink-0">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

// ══════════════════════════════════════════════
// BULK OPERATIONS CONSOLE
// ══════════════════════════════════════════════
export function BulkOperationsConsole({ token }) {
  const [entity, setEntity] = useState('assignments');
  const [operation, setOperation] = useState('update_field');
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [ids, setIds] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const execute = async () => {
    const idList = ids.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    if (!idList.length) { toast.error('Enter at least one ID'); return; }
    setExecuting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/bulk-operations`, {
        operation, entity, ids: idList, field, value,
      }, { headers });
      setResult(res.data);
      toast.success(`Bulk ${operation}: ${res.data.modified || res.data.deleted || 0} items affected`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setExecuting(false); }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-3xl" data-testid="bulk-operations">
        <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Bulk Operations</h2>
        <p className="text-xs text-muted-foreground mb-4">Perform batch actions on assignments, properties, products, or vendors.</p>
        <div className="space-y-4 border rounded-xl p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Entity</Label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['assignments', 'properties', 'products', 'vendors'].map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Operation</Label>
              <Select value={operation} onValueChange={setOperation}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="update_field">Update Field</SelectItem>
                  <SelectItem value="add_tag">Add Tag</SelectItem>
                  <SelectItem value="remove_tag">Remove Tag</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {(operation === 'update_field') && (
            <div>
              <Label className="text-xs">Field Name</Label>
              <Input value={field} onChange={e => setField(e.target.value)} placeholder="e.g., status, notes, layer" className="h-9 text-xs" />
            </div>
          )}
          {operation !== 'delete' && (
            <div>
              <Label className="text-xs">Value</Label>
              <Input value={value} onChange={e => setValue(e.target.value)} placeholder="New value or tag name" className="h-9 text-xs" />
            </div>
          )}
          <div>
            <Label className="text-xs">Item IDs (one per line or comma-separated)</Label>
            <textarea value={ids} onChange={e => setIds(e.target.value)} rows={4}
              className="w-full rounded-md border bg-background px-3 py-2 text-xs font-mono"
              placeholder="abc-123&#10;def-456&#10;ghi-789" />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={execute} disabled={executing || !ids.trim()} className={`gap-1.5 ${operation === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}`}>
              {executing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Execute {operation.replace('_', ' ')}
            </Button>
            {result && <span className="text-xs text-emerald-600 font-medium">{result.modified || result.deleted || 0} items affected</span>}
          </div>
        </div>
        <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200">
          <p className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Bulk operations are logged in the Audit Trail and cannot be undone.
          </p>
        </div>
      </div>
    </ScrollArea>
  );
}

// ══════════════════════════════════════════════
// TENANT HEALTH DASHBOARD
// ══════════════════════════════════════════════
export function TenantHealthDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/tenant-health`, { headers })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-5xl" data-testid="tenant-health-dashboard">
        <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Tenant Health</h2>
        <p className="text-xs text-muted-foreground mb-4">Overview of all tenants: usage, adoption, and health metrics.</p>
        {data?.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="border rounded-xl p-4 bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.total_tenants}</div><div className="text-[10px] text-muted-foreground">Tenants</div></div>
            <div className="border rounded-xl p-4 bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.total_users}</div><div className="text-[10px] text-muted-foreground">Users</div></div>
            <div className="border rounded-xl p-4 bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.total_properties}</div><div className="text-[10px] text-muted-foreground">Properties</div></div>
            <div className="border rounded-xl p-4 bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.total_assignments}</div><div className="text-[10px] text-muted-foreground">Assignments</div></div>
          </div>
        )}
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2.5 font-semibold">Tenant</th>
                <th className="p-2.5 font-semibold text-center">Plan</th>
                <th className="p-2.5 font-semibold text-center">Users</th>
                <th className="p-2.5 font-semibold text-center">Properties</th>
                <th className="p-2.5 font-semibold text-center">Assignments</th>
                <th className="p-2.5 font-semibold text-center">Features</th>
                <th className="p-2.5 font-semibold">Last Login</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(data?.tenants || []).map(t => (
                <tr key={t.tenant_id} className="hover:bg-muted/20">
                  <td className="p-2.5">
                    <span className="font-medium block">{t.name}</span>
                    <span className="text-[9px] text-muted-foreground">{t.slug}</span>
                  </td>
                  <td className="p-2.5 text-center"><Badge variant="outline" className={`text-[8px] ${t.plan === 'trial' ? 'text-amber-600 border-amber-300' : ''}`}>{t.plan || 'free'}</Badge></td>
                  <td className="p-2.5 text-center tabular-nums">{t.user_count}</td>
                  <td className="p-2.5 text-center tabular-nums">{t.property_count}</td>
                  <td className="p-2.5 text-center tabular-nums">{t.assignment_count}</td>
                  <td className="p-2.5 text-center tabular-nums">{t.features_enabled}</td>
                  <td className="p-2.5 text-xs text-muted-foreground">{t.last_login ? new Date(t.last_login).toLocaleDateString() : 'Never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </ScrollArea>
  );
}
